// Guards the lazy ticket chunk (D7). Lives in the MAIN bundle — it must not
// import anything from the ticket chunk (uqr, badges), or the guard itself
// would be behind the door it guards.
//
//   - TicketBoundary: a chunk 404 (stale HTML after a deploy, flaky network)
//     or a render crash inside the section must never take the portal down:
//     render nothing, warn once, the attachments below keep working.
//   - TicketSectionFallback: reserved-height Suspense placeholder (section
//     title bar + one card-shaped block ≈ 24 rem) so the attachments do not
//     jump when the chunk lands.

import { Component, type CSSProperties, type ErrorInfo, type ReactNode } from "react";

type BoundaryProps = {
  children: ReactNode;
  /** Fired once when the section is dropped, so the host can stop treating the ticket as present. */
  onFailed?: (() => void) | undefined;
};
type BoundaryState = { failed: boolean };

export class TicketBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { failed: false };
  private warned = false;

  static getDerivedStateFromError(): BoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (this.warned) return;
    this.warned = true;
    console.warn("[portal] ticket section failed to load — rendering the portal without it", error, info.componentStack);
    this.props.onFailed?.();
  }

  override render(): ReactNode {
    return this.state.failed ? null : this.props.children;
  }
}

/** Height the finished section roughly occupies for one card (title + QR panel + hint + actions). */
export const TICKET_FALLBACK_MIN_HEIGHT = "24rem";

export function TicketSectionFallback({ cardStyle }: { cardStyle: CSSProperties }) {
  return (
    <section
      data-testid="portal-ticket-fallback"
      aria-busy="true"
      aria-hidden="true"
      className="mb-8"
      style={{ minHeight: TICKET_FALLBACK_MIN_HEIGHT }}
    >
      <div className="mb-4 h-7 w-40 rounded-md bg-current opacity-10 motion-safe:animate-pulse motion-reduce:animate-none" />
      <div className="border" style={{ ...cardStyle, minHeight: "20rem" }} />
    </section>
  );
}
